import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { fugueLabProjects, ClientType } from '@/data/portfolio'
import { PortfolioCard } from '@/components/PortfolioCard'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, Music, Zap, Smartphone, Pencil, Target, Users } from 'lucide-react'
import { FESTIVAL_LANE, DFY_BUNDLES } from '@gjl/voice/pricing'

const festivalPlatform = FESTIVAL_LANE.find((p) => p.id === 'festival-platform')!
const concierge = DFY_BUNDLES.find((b) => b.id === 'concierge')!

export default function FestivalWebsitesPage() {
  const breadcrumbs = generateBreadcrumbs('/services/festival-websites', breadcrumbLabels)

  useSEO({
    title: 'Festival Website Design & Development',
    description:
      'Modern, fast, mobile-optimized websites for classical music festivals. Convert visitors into enrolled students with websites that reflect your programs.',
    url: '/services/festival-websites',
    canonical: '/services/festival-websites',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Festival Website Design & Development',
      description:
        'Custom websites for classical music festivals. Modern, fast, and mobile-optimized to convert visitors into enrolled students.',
      url: '/services/festival-websites',
    }),
  })

  // Filter for festival projects only
  const festivalProjects = fugueLabProjects.filter((project) =>
    project.clientTypes?.some((type) => type === ClientType.Festival)
  )

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const packageRef = useScrollReveal<HTMLDivElement>('.package-content')
  const portfolioRef = useStaggeredGrid<HTMLDivElement>('.portfolio-item', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.1 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Modern Websites for Classical Music Festivals</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your festival creates transformative musical experiences. But if your website is outdated, slow, or confusing,
          you&apos;re losing qualified applicants before they ever hit &quot;apply.&quot;
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          We build modern, fast, mobile-optimized festival websites that reflect the quality of your programs and
          convert visitors into enrolled students—all in 6-8 weeks.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
        </div>
        <div className="text-muted-foreground flex flex-col gap-2 pt-2 text-sm">
          <div className="flex items-center justify-center gap-2">
            <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
            <span>15-minute strategy call, no obligation</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
            <span>Built by a musician + software engineer</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Check className="text-primary h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
            <span>6-8 week timeline from kickoff to launch</span>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Is Your Festival Website Costing You Qualified Applicants?</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              problem: 'Outdated Design',
              description:
                'Your website looks like it was built in 2010. Prospective students see it and question whether your festival is still active.',
            },
            {
              problem: 'Broken on Mobile',
              description:
                "Gen Z applicants browse on phones. If your site doesn't work on mobile, they'll move on to the next festival.",
            },
            {
              problem: 'Slow Load Times',
              description:
                'Your site takes 10+ seconds to load. Studies show 53% of visitors abandon sites that take longer than 3 seconds.',
            },
            {
              problem: 'Confusing Navigation',
              description:
                "Applicants can't find your program details, faculty list, or application link. They give up and apply elsewhere.",
            },
            {
              problem: 'Impossible to Update',
              description:
                "You need to hire a developer every time you want to update your faculty list or schedule. It's expensive and slow.",
            },
            {
              problem: 'No Clear Enrollment Path',
              description: 'There\'s no obvious "Apply Now" button. Interested students don\'t know what to do next.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-4 text-center italic">
          Sound familiar? You&apos;re not alone. Most festival websites were built years ago and haven&apos;t been
          updated to meet modern standards—or student expectations.
        </p>
      </section>

      {/* Solution Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">The Better Way: A Modern Festival Website</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            We build modern, fast, beautiful websites specifically for classical music festivals. Sites that look
            professional, work flawlessly on mobile, and make it easy for students to apply.
          </p>
        </div>

        <div ref={packageRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-8">
          <div className="package-content space-y-6">
            <h3 className="text-center text-xl font-semibold">The Festival Website Package</h3>
            <p className="text-muted-foreground text-center">
              Everything your festival needs for a professional online presence that converts visitors into enrolled
              students. Most festivals run on {festivalPlatform.name} at {festivalPlatform.price}, with {concierge.name}{' '}
              at {concierge.price} for a done-for-you launch and design. Custom builds are quoted on the call. Full
              plans on the{' '}
              <Link to="/packages" className="text-primary hover:underline">
                pricing page
              </Link>
              .
            </p>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold">What&apos;s Included:</h4>
              <ul className="space-y-3">
                {[
                  'Full website design & development (Next.js + Tailwind for speed and modern design)',
                  'CMS integration so you can update programs, faculty, and schedules yourself—no developer needed',
                  'Core pages that convert: Home, Programs, Faculty, Schedule, FAQ, Apply, Contact',
                  'Mobile-optimized so applicants can browse seamlessly on any device',
                  'Fast loading times (under 3 seconds—no one waits for slow websites)',
                  'SEO setup so presenters and students can find you on Google',
                  'Email integration for contact forms and inquiries',
                  '2 weeks of post-launch support for any adjustments or questions',
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-border space-y-2 border-t pt-6">
              <p className="font-semibold">Timeline: 6-8 weeks from kickoff to launch</p>
              <p className="text-muted-foreground text-sm">
                Week 1-2: Discovery & Planning
                <br />
                Week 3-5: Design & Development
                <br />
                Week 6-7: Content Migration & Testing
                <br />
                Week 8: Launch & Training
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      {festivalProjects.length > 0 && (
        <section className="space-y-8">
          <div className="space-y-4 text-center">
            <h2 className="text-fluid-h2">Festival Websites We&apos;ve Built</h2>
            <p className="text-fluid-body text-muted mx-auto max-w-2xl">
              See how we&apos;ve helped classical music festivals modernize their digital presence and grow enrollment.
            </p>
          </div>
          <div ref={portfolioRef} className="columns-1 gap-8 space-y-8 md:columns-2 lg:columns-3">
            {festivalProjects.map((project) => (
              <div key={project.slug} className="portfolio-item">
                <PortfolioCard {...project} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Festivals Choose Us</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: Music,
              title: 'Built by Musicians',
              description:
                "We're not generic web developers. We're musicians (Interlochen alum, CIM grad) who understand conservatory training, festival operations, and what students expect.",
            },
            {
              icon: Zap,
              title: 'Fast & Modern',
              description:
                'Built on Next.js—the same technology used by Hulu, Netflix, and TikTok. Your site will be lightning-fast and future-proof.',
            },
            {
              icon: Smartphone,
              title: 'Mobile-First Design',
              description:
                'Gen Z students browse on phones. We design for mobile first, then adapt for desktop—ensuring a seamless experience everywhere.',
            },
            {
              icon: Pencil,
              title: 'Easy to Update',
              description:
                'Update your programs, faculty, and schedules yourself through a simple CMS. No developer needed for routine content changes.',
            },
            {
              icon: Target,
              title: 'Conversion-Focused',
              description:
                'Every page is designed with one goal: get qualified students to apply. Clear CTAs, intuitive navigation, and obvious next steps.',
            },
            {
              icon: Users,
              title: 'White-Glove Service',
              description:
                "You'll work directly with Garrett (founder). No offshore teams, no project managers who don't understand music.",
            },
          ].map((item, idx) => {
            const Icon = item.icon
            return (
              <motion.div
                key={idx}
                className="benefit-card border-border/50 from-primary/5 hover:border-primary/30 space-y-3 rounded-2xl border bg-gradient-to-b to-transparent p-6 text-center transition-all duration-300 hover:shadow-md"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="bg-primary/10 text-primary mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <Icon className="h-6 w-6" />
                </motion.div>
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'How long does it take to build a festival website?',
              answer:
                '6-8 weeks from kickoff to launch. We work in sprints: Discovery (2 weeks), Design & Development (3 weeks), Content Migration & Testing (2 weeks), Launch & Training (1 week).',
            },
            {
              question: 'Can we update the website ourselves after launch?',
              answer:
                "Yes! We include CMS (Content Management System) integration so you can update programs, faculty, schedules, and other content yourself—no developer needed. We'll train your team on how to use it during launch week.",
            },
            {
              question: 'Do you work with festivals outside the US?',
              answer:
                "Absolutely. We've worked with festivals internationally. All we need is clear communication (Zoom calls work great across time zones) and content from your team.",
            },
            {
              question: 'What if we need ongoing support after launch?',
              answer: `Most festivals run on ${festivalPlatform.name} at ${festivalPlatform.price}: website, applications, email, and ticketing, light-managed by our team year-round. ${concierge.name} at ${concierge.price} adds branding, a done-for-you launch, and a design retainer, aligned with your recruitment cycle. Fully custom builds are quoted on a call.`,
            },
            {
              question: 'Can you integrate with our existing application system?',
              answer:
                "Yes. We can integrate with most systems (Acceptd, Submit able, custom forms). If you don't have an application system yet, we can build one as part of our Application & Enrollment Funnel package.",
            },
            {
              question: 'What makes you different from other web designers?',
              answer:
                "We're musicians serving festivals. Garrett is an Interlochen alum and CIM graduate who understands conservatory training and festival operations. We speak your language and build systems tailored to the festival lifecycle—not generic websites.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Modernize Your Festival&apos;s Website?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute festival digital audit. We&apos;ll review your current site, identify what&apos;s
            costing you applicants, and show you exactly how we can help.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No sales pitch. Just honest feedback on your website and clear next steps if you want our help.
          </p>
        </div>
      </section>
    </div>
  )
}
